import {
  CryptoCurrencyEnum,
  CurrencyDictionaryResponse_CryptoCurrencyEnum,
  CurrencyDictionaryResponse_Int32,
  CurrencyTypeEnum,
  DictionariesService,
} from '@acme/api';

class CurrencyStore {
  private currencies: CurrencyDictionaryResponse_Int32[] | null = null;
  private cryptoCurrencies:
    | CurrencyDictionaryResponse_CryptoCurrencyEnum[]
    | null = null;
  private loadingPromise: Promise<CurrencyDictionaryResponse_Int32[]> | null =
    null;
  private cryptoLoadingPromise: Promise<
    CurrencyDictionaryResponse_CryptoCurrencyEnum[]
  > | null = null;
  private error: Error | null = null;

  async getCurrencies(): Promise<CurrencyDictionaryResponse_Int32[]> {
    if (this.currencies) {
      return this.currencies;
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = (async () => {
      try {
        const response =
          await DictionariesService.getApiV1DictionariesCurrencies();
        if (!response.data) {
          throw new Error('No data received from currencies API');
        }
        this.currencies = response.data;
        return this.currencies;
      } catch (err) {
        this.error =
          err instanceof Error ? err : new Error('Failed to load currencies');
        throw this.error;
      } finally {
        this.loadingPromise = null;
      }
    })();

    return this.loadingPromise;
  }

  getCurrencyById(
    id: CurrencyTypeEnum
  ): CurrencyDictionaryResponse_Int32 | undefined {
    return this.currencies?.find((currency) => currency.id === id);
  }

  getCurrencyByCode(
    code: string
  ): CurrencyDictionaryResponse_Int32 | undefined {
    return this.currencies?.find((currency) => currency.name === code);
  }

  async getCryptoCurrencies(): Promise<
    CurrencyDictionaryResponse_CryptoCurrencyEnum[]
  > {
    if (this.cryptoCurrencies) {
      return this.cryptoCurrencies;
    }

    if (this.cryptoLoadingPromise) {
      return this.cryptoLoadingPromise;
    }

    this.cryptoLoadingPromise = (async () => {
      try {
        const response =
          await DictionariesService.getApiV1DictionariesCryptoCurrencies();
        if (!response.data) {
          throw new Error('No data received from crypto currencies API');
        }
        this.cryptoCurrencies = response.data;
        return this.cryptoCurrencies;
      } catch (err) {
        this.error =
          err instanceof Error ? err : new Error('Failed to load currencies');
        throw this.error;
      } finally {
        this.cryptoLoadingPromise = null;
      }
    })();

    return this.cryptoLoadingPromise;
  }

  getCryptoCurrencyById(
    id: CryptoCurrencyEnum
  ): CurrencyDictionaryResponse_CryptoCurrencyEnum | undefined {
    return this.cryptoCurrencies?.find((currency) => currency.id === id);
  }

  getCryptoCurrencyByCode(
    code: string
  ): CurrencyDictionaryResponse_CryptoCurrencyEnum | undefined {
    return this.cryptoCurrencies?.find((currency) => currency.name === code);
  }
}

export const currencyStore = new CurrencyStore();
